import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';

import * as d3 from 'd3';
import * as d3Axis from 'd3-axis';
import { gsap } from 'gsap';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';

import { ChartingService } from '../../../services';
import { AccessorType, Config, DataPointScaled, DataType, Dimensions, Serie, TooltipData, TooltipValue, LegendTooltipPos, LegendTooltipPosEnum } from '../../../models';

class OutsideBoundary {
    private top2: boolean;
    private top: boolean;
    private right: boolean;
    private right2: boolean;
    private bottom2: boolean;
    private bottom: boolean;
    private left: boolean;
    private left2: boolean;

    /**
     * @param {number} bw Boundary width
     * @param {number} bh Boundary height
     * @param {number} px x-coordinate of the point
     * @param {number} py y-coordinate of the point
     * @param {number} w Tooltip width
     * @param {number} h Tooltip height
     * @param {number} as Arrow size
     */
    public bestPosition(bw: number, bh: number, px: number, py: number, w: number, h: number, as: number): LegendTooltipPos {
        // Y'a t'il de la place pour le mettre
        // à droite
        this.right = (px + as + w) < bw;
        // à gauche
        this.left = (px + as - w) > 0;
        // au-dessous
        this.bottom = (py + as + h) < bh;
        // au-dessus
        this.top = (py + as - h) > 0;
        this.right2 = (px + (as + w) / 2) < bw;
        this.left2 = (px - (as + w) / 2) > 0;
        this.bottom2 = (py + (as + h) / 2) < bh;
        this.top2 = (py - (as + h) / 2) > 0;

        if (this.right && this.bottom2 && this.top2) {
            return LegendTooltipPosEnum.RIGHT;
        }
        else if (this.left && this.bottom2 && this.top2) {
            return LegendTooltipPosEnum.LEFT;
        }
        else if (this.top && this.left2 && this.right2) {
            return LegendTooltipPosEnum.TOP;
        }

        return LegendTooltipPosEnum.BOTTOM;
    }
}

@Component({
    selector: 'aa-timeline-raw',
    templateUrl: './timeline-raw.component.html',
    styleUrls: [
        './timeline-raw.component.scss'
    ],
    encapsulation: ViewEncapsulation.None,
    providers: [
        ChartingService
    ],
    standalone: false
})
export class TimelineRawComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() valueToEliminate?: number = -0;
    @Input() labelForEliminatedValue?: string = 'No record';
    @Input() data: DataType[];
    @Input() cfg: Config;
    @Input() series: Serie[];
    @Input() hasDataPoints: boolean;
    @Input() shiftClient: boolean = false;
    @Input('curve') set setInterpolation(value: number) {
        switch (value) {
            case 1:
                this.interpolation = d3.curveLinear;
                break;

            case 0:
            default:
                this.interpolation = d3.curveMonotoneX;
                break;
        }
    }
    @Input() dottedLine: boolean = false;
    private interpolation?: d3.CurveFactory | d3.CurveFactoryLineOnly = d3.curveMonotoneX;
    @ViewChild('container', { static: false }) set setContainer(elementRef: ElementRef) {
        if (elementRef) {
            this.container = elementRef.nativeElement;
        }
    }
    protected container: HTMLElement;
    public config: Config;
    public xFormatTick: (date: any) => string;
    public yFormatTick: (value: number) => (string);
    // #region tooltip
    private gpeTooltip: any;
    private svgTooltip: any;
    private pathTooltip: any;
    private titleTooltip: any;
    public tooltipData: TooltipData;
    public posX: number = -1000;
    public posY: number = -1000;
    private tooltipSVGX: number = -1000;
    private tooltipSVGY: number = -1000;
    private guideVer: Element;
    private guideHor: Element;
    private static duration: number = 0.5;
    private tooltipWidth: number;
    private tooltipMaxWidth: number;
    private tooltipHeight: number;
    private tooltipMaxHeight: number;
    private tooltipArrowSize: number = 7;
    private orientation: string = LegendTooltipPosEnum.RIGHT;
    public tooltipRadius: number = 4;
    public tooltipTitleHeight: number = 30;
    public tooltipLabelHeight: number = 22;
    // #endregion
    public calculatedHasDataPoints: boolean;
    public switchGridVerOn: boolean = true;
    public switchGridHorOn: boolean = true;
    public isReady: boolean;
    public xScale: d3.ScaleTime<number, number>;
    public yScale: d3.ScaleLinear<number, number>;
    public y0AccessorScaled: AccessorType;
    public keyAccessor: AccessorType = (i: number) => i;
    public dimensions: Dimensions;
    public visibleSeries: Serie[];
    private destroyator = new Subject();
    private svgChart: any;
    // Positionnement du SVG
    private gpeChart: any;
    private xLeft: number;
    private xRight: number;
    private yTop: number;
    private yBottom: number;
    private shiftLeft: number = 0;
    private shiftTop: number = 0;
    private gpeLegendTextItems: any;
    private maxLegendWidth: number;
    private pendingRedraw: boolean = false;

    constructor(
        private chartingService: ChartingService
    ) {
    }

    ngOnInit(): void {
        fromEvent(window, 'resize')
            .pipe(
                debounceTime(250),
                tap(() => {
                    Promise.resolve(null).then(() => {
                        this.reDrawChart();
                    });
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges.cfg?.previousValue === undefined && simpleChanges.cfg?.currentValue !== undefined) {
            this.initConfig();
        }
        // Redraw chart when data or series (or cfg after first init) change
        const dataChanged = !!simpleChanges.data && !simpleChanges.data.firstChange;
        const seriesChanged = !!simpleChanges.series && !simpleChanges.series.firstChange;
        const cfgChanged = !!simpleChanges.cfg && !simpleChanges.cfg.firstChange;
        if (dataChanged || seriesChanged || cfgChanged) {
            if (this.container) {
                this.reDrawChart();
            }
            else {
                this.pendingRedraw = true;
            }
        }
    }

    ngAfterViewInit(): void {
        Promise.resolve(null).then(() => {
            this.drawChart();
            if (this.pendingRedraw) {
                this.pendingRedraw = false;
                this.reDrawChart();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    /**
     * Traitements indépendants des dimensions du container
     */
    private initConfig(): void {
        this.chartingService.config = this.cfg;
        this.xFormatTick = this.chartingService.xTimeFormatTick;
        this.yFormatTick = this.chartingService.yFormatTick;
        if (!this.chartingService.config.title.draw) {
            this.chartingService.config.dimensions.marginTop /= 2;
        }

        this.dimensions = {
            ...this.dimensions,
            ...this.chartingService.config.dimensions
        };
    }

    private reDrawChart(): void {
        if (!this.data || !this.series) { return; }
        d3.select(this.container).select('svg').remove();
        this.drawChart();
    }

    private drawChart(): void {
        this.initSvg();
        this.adjustScales();

        this.drawListeningZone();

        this.drawXAxis();
        this.drawYAxis();
        this.drawSeries();
        this.drawLegend();
        this.drawGuides();
        this.drawTooltip();
    }

    private initSvg(): void {
        this.adjustDimensions();
        this.svgChart = d3.select(this.container)
            .append('svg')
            .attr('class', 'svgChart')
            .attr('width', this.dimensions.width)
            .attr('height', this.dimensions.height);
        // .attr('viewBox', [0, 0, this.dimensions.width, this.dimensions.height].join(','));
        this.svgChart
            .append('defs')
            .append('filter')
            .attr('id', 'shadow')
            .append('feDropShadow')
            .attr('dx', 0.5)
            .attr('dy', 0.5)
            .attr('stdDeviation', 3);
        this.gpeChart = this.svgChart
            .append('g')
            .attr('class', 'gpeChart')
            .attr('transform', `translate(${this.dimensions.marginLeft},${this.dimensions.marginTop})`);
    }

    private adjustDimensions(): void {
        const { x, y, width, height } = this.container.getBoundingClientRect();

        if (this.shiftClient) {
            this.shiftLeft = x;
            this.shiftTop = y;
        }

        this.dimensions = {
            ...this.dimensions,
            width: width,
            height: height,
            boundedWidth: Math.max(width - this.dimensions.marginLeft - this.dimensions.marginRight, 0),
            boundedHeight: Math.max(height - this.dimensions.marginTop - this.dimensions.marginBottom, 0)
        };

        this.yBottom = height / 2;
        this.yTop = -1 * this.yBottom;
        this.xRight = width / 2;
        this.xLeft = -1 * this.xRight;
    }

    private adjustScales(): void {
        this.series.forEach((serie: Serie, idx: number) => {
            serie.id = idx;
            serie.dataPoints = this.data;
        });
        this.visibleSeries = this.series.filter((s: Serie) =>
            s.isVisible
        );
        const mins: number[] = [];
        const maxs: number[] = [];
        if (this.visibleSeries.length > 0) {
            if (this.chartingService.config?.type === 'percentage') {
                mins.push(0);
                maxs.push(100);
            }
            else {
                this.visibleSeries.forEach((serie: Serie) => {
                    mins.push(d3.min(this.data, serie.yAccessor));
                    maxs.push(d3.max(this.data, serie.yAccessor));
                });
            }

            this.xScale = d3.scaleTime()
                .domain(d3.extent(this.data, this.visibleSeries[0].xAccessor))
                .range([0, this.dimensions.boundedWidth])
                .nice();
            this.yScale = d3.scaleLinear()
                .domain([d3.min(mins), d3.max(maxs)])
                .range([this.dimensions.boundedHeight, 0])
                .nice();

            this.visibleSeries.forEach((serie: Serie) => {
                serie.y0AccessorScaled = () =>
                    this.yScale(this.yScale.domain()[d3.min(mins)]);
                serie.xAccessorScaled = (d: DataType) =>
                    this.xScale(serie.xAccessor(d));
                serie.yAccessorScaled = (d: DataType) =>
                    this.yScale(serie.yAccessor(d));
            });
            this.visibleSeries.forEach((serie: Serie) =>
                serie.dpScaled = this.data.map((d: DataType) =>
                    new DataPointScaled(
                        serie.xAccessorScaled(d),
                        serie.yAccessorScaled(d), //  !== this.valueToEliminate ? serie.yAccessorScaled(d) : -1000,
                        serie.yAccessor(d) !== this.valueToEliminate ? serie.config.color : 'transparent',
                        serie.yAccessor(d) !== this.valueToEliminate ? this.tooltipRadius : 0
                    )
                )
            );
        }
    }

    private drawXAxis(): void {
        const nbTicks: number = this.dimensions.boundedWidth < 600
            ? this.dimensions.boundedWidth / 100
            : this.dimensions.boundedWidth / 250;

        const gpeXaxis = this.gpeChart
            .insert('g', '.listening-zone')
            .attr('class', 'gpeXaxis')
            .attr('transform', `translate(0,${this.dimensions.boundedHeight})`);

        const xAxis = d3Axis.axisBottom(this.xScale)
            .ticks(nbTicks, this.xFormatTick);

        gpeXaxis.call(xAxis)
            .call((g: any) => {
                g.selectAll('path')
                    .attr('stroke', null)
                    .classed('domain');
                g.selectAll('.tick text')
                    .attr('fill', null)
                    .attr('x', 0)
                    .attr('dy', 7)
                    .attr('transform', 'rotate(-45)');
                g.selectAll('.tick line')
                    .attr('stroke', null);
            })
            .append('text')
            .attr('class', 'label')
            .attr('x', this.dimensions.boundedWidth / 2)
            .attr('y', 40)
            .text(this.chartingService.config.xLabel);

    }

    private drawYAxis(): void {
        const nbTicks: number = this.dimensions.boundedHeight / 50;
        this.gpeChart
            .selectAll('.gpeYaxis')
            .remove();
        const gpeYaxis = this.gpeChart
            .insert('g', '.listening-zone')
            .attr('class', 'gpeYaxis');

        const yAxis = d3Axis.axisLeft(this.yScale)
            .ticks(nbTicks, this.yFormatTick);

        gpeYaxis.call(yAxis)
            .call((g: any) => {
                g.selectAll('path')
                    .attr('stroke', null)
                    .classed('domain');
                g.selectAll('.tick text')
                    .attr('fill', null)
                    .attr('x', -10)
                    .attr('dy', 3);
                g.selectAll('.tick line')
                    .attr('stroke', null);
            })
            .append('text')
            .attr('class', 'label')
            .attr('transform', 'rotate(-90)')
            .attr('x', -this.dimensions.boundedHeight / 2)
            .attr('y', -50)
            .text(this.chartingService.config.yLabel);
    }

    private drawSeries(): void {
        this.gpeChart
            .selectAll('.gpeSeries')
            .remove();
        const gpeSeries = this.gpeChart
            .insert('g', '.listening-zone')
            .attr('class', 'gpeSeries');
        const serieGroups = gpeSeries
            .selectAll('g')
            .data(this.visibleSeries)
            .enter()
            .append('g')
            .attr('class', 'gpeSerie');

        if (this.dottedLine) {
            serieGroups
                .append('path')
                .attr('class', 'line dotted')
                .attr('stroke', (serie: Serie) =>
                    serie.config.color
                )
                .attr('stroke-width', (serie: Serie) =>
                    serie.config.width ?? 1
                )
                .attr('d', (serie: Serie) =>
                    this.lineGenerator(serie, false)
                );
        }

        serieGroups
            .append('path')
            .attr('class', 'line')
            .attr('stroke', (serie: Serie) =>
                serie.config.color
            )
            .attr('stroke-width', (serie: Serie) =>
                serie.config.width ?? 1
            )
            .attr('d', (serie: Serie) =>
                this.lineGenerator(serie, this.dottedLine)
            );

        serieGroups
            .append('g')
            .attr('class', 'gpeCircles')
            .selectAll('circle')
            .data((serie: Serie) =>
                serie.dpScaled
            )
            .enter()
            .append('circle')
            .attr('cx', (d: DataPointScaled) =>
                d.xScaled
            )
            .attr('cy', (d: DataPointScaled) =>
                d.yScaled
            )
            .attr('r', (d: DataPointScaled) =>
                d.radius
            )
            .attr('stroke', (d: DataPointScaled) =>
                d.color
            );
    }

    private drawLegend(): void {
        this.svgChart
            .selectAll('.gpeLegend')
            .remove();

        const gpeLegend = this.svgChart
            .insert('g', '.listening-zone')
            .attr('class', 'gpeLegend')
            .attr('transform', `translate(${this.dimensions.marginLeft / 2},0)`);

        const gpeLegendItems = gpeLegend
            .selectAll('svg')
            .data(this.series)
            .enter()
            .append('svg')
            .attr('height', 32);
        gpeLegendItems
            .append('circle')
            .attr('cx', 10)
            .attr('cy', 16)
            .attr('r', this.tooltipRadius)
            .attr('stroke', (serie: Serie) =>
                serie.config.color
            );

        const textOffset: number = 20;
        const marginRightLeft: number = 7;
        this.gpeLegendTextItems = gpeLegendItems
            .append('text')
            .attr('x', textOffset)
            .attr('y', 20)
            .style('text-anchor', 'start')
            .attr('class', 'legend legend-item')
            .attr('stroke', (serie: Serie) =>
                serie.config.color
            )
            .style('text-decoration', (serie: Serie) =>
                serie.isVisible ? '' : 'line-through'
            )
            .text((serie: Serie) =>
                serie.label
            );
        const legendWidth = Array.from(this.container.querySelectorAll('text.legend.legend-item'))
            .map((eleRef: SVGSVGElement) =>
                eleRef.getBBox().width
            );
        this.maxLegendWidth = Math.max(...legendWidth);
        this.tooltipWidth = 150 + this.maxLegendWidth;
        this.tooltipMaxWidth = 2 * this.tooltipWidth;

        const translates: number[] = legendWidth
            .map((width: number) =>
                width + textOffset + 2 * marginRightLeft
            )
            .reduce((a, x, i) => [...a, x + (a[i - 1] || 0)], []);

        gpeLegendItems
            .attr('x', (_: Serie, idx: number) =>
                idx === 0 ? 0 : translates[idx - 1]
            )
            .attr('y', 0)
            .on(
                'click',
                (_: any, serie: Serie) =>
                    this.toggleSerie(serie)
            );
    }

    private drawListeningZone(): void {
        this.svgChart
            .selectAll('.listening-zone')
            .remove();
        this.svgChart
            .append('rect')
            .attr('class', 'listening-zone')
            .attr('x', this.dimensions.marginLeft)
            .attr('y', this.dimensions.marginTop)
            .attr('width', this.dimensions.boundedWidth)
            .attr('height', this.dimensions.boundedHeight)
            .on(
                'mouseover',
                () =>
                    this.toggleTooltip(true)
            )
            .on(
                'mousemove',
                (event: MouseEvent) =>
                    this.chartMousemove(event)
            )
            .on(
                'mouseout',
                () =>
                    this.toggleTooltip(false)
            );
    }

    /**
     *
     * @param {boolean} state : définit le statut visible ou non des composants
     */
    private toggleTooltip(state: boolean) {
        if (!state) {
            switch (this.orientation) {
                case LegendTooltipPosEnum.TOP:
                    gsap.timeline()
                        .to('#tooltip-box', { duration: 2 * TimelineRawComponent.duration, css: { y: this.tooltipMaxHeight + this.dimensions.height }, ease: 'none' }, 0);

                    break;
                case LegendTooltipPosEnum.RIGHT:
                    gsap.timeline()
                        .to('#tooltip-box', { duration: 2 * TimelineRawComponent.duration, css: { x: -this.tooltipMaxWidth }, ease: 'none' }, 0);

                    break;

                case LegendTooltipPosEnum.BOTTOM:
                    gsap.timeline()
                        .to('#tooltip-box', { duration: 2 * TimelineRawComponent.duration, css: { y: -this.tooltipMaxHeight }, ease: 'none' }, 0);

                    break;

                case LegendTooltipPosEnum.LEFT:
                    gsap.timeline()
                        .to('#tooltip-box', { duration: 2 * TimelineRawComponent.duration, css: { x: this.tooltipMaxWidth + this.dimensions.width }, ease: 'none' }, 0);

                    break;
            }
        }

        this.gpeChart
            .selectAll('.tooltip')
            .classed('unmasked', state);
    }

    private drawGuides(): void {
        this.gpeChart
            .select('line.guide-hor')
            .remove();
        this.guideHor = this.gpeChart
            .append('line')
            .attr('id', 'guide-hor')
            .attr('class', 'tooltip guide-hor')
            .attr('x1', 0)
            .attr('y1', this.posY)
            .attr('x2', this.posX)
            .attr('y2', this.posY);
        this.gpeChart
            .select('line.guide-ver')
            .remove();
        this.guideVer = this.gpeChart
            .append('line')
            .attr('id', 'guide-ver')
            .attr('class', 'tooltip guide-ver')
            .attr('x1', this.posX)
            .attr('y1', 0)
            .attr('x2', this.posX)
            .attr('y2', this.dimensions.boundedHeight);
    }

    private drawTooltip(): void {
        this.gpeChart
            .select('g.tooltip-box')
            .remove();
        this.gpeTooltip = this.gpeChart
            .append('g')
            .attr('id', 'tooltip-box')
            .attr('class', 'tooltip-box');
        this.pathTooltip = this.gpeTooltip
            .append('path')
            .style('filter', 'url(#shadow)');
        this.svgTooltip = this.gpeTooltip
            .append('svg')
            .attr('x', this.tooltipSVGX)
            .attr('y', this.tooltipSVGY);
        this.titleTooltip = this.svgTooltip
            .append('text')
            .attr('x', (this.tooltipWidth - this.tooltipArrowSize) / 2)
            .attr('y', 20)
            .attr('class', 'tooltip-title');
        this.svgTooltip
            .append('line')
            .attr('x1', 15)
            .attr('y1', this.tooltipTitleHeight)
            .attr('x2', this.tooltipWidth - 15)
            .attr('y2', this.tooltipTitleHeight);
    }

    private drawTooltipValues(): void {
        this.svgTooltip
            .selectAll('svg')
            .remove();
        const gpeValues = this.svgTooltip
            .selectAll('svg')
            .data(this.tooltipData.values)
            .enter()
            .append('svg')
            .attr('class', 'values')
            .attr('x', 20)
            .attr('y', (_: any, idx: number) =>
                this.tooltipLabelHeight * idx + this.tooltipTitleHeight
            );

        gpeValues
            .append('circle')
            .attr('cx', 10)
            .attr('cy', 15)
            .attr('r', this.tooltipRadius)
            .attr('stroke', (tooltipValue: TooltipValue) =>
                tooltipValue.color
            );
        gpeValues
            .append('text')
            .attr('x', 20)
            .attr('y', 20)
            .style('text-anchor', 'start')
            .attr('class', 'legend-item')
            .style('stroke', (tooltipValue: TooltipValue) =>
                tooltipValue.color
            )
            .text((tooltipValue: TooltipValue) =>
                tooltipValue.label
            );
        gpeValues
            .append('text')
            .attr('x', this.tooltipWidth - 50)
            .attr('y', 20)
            .style('text-anchor', 'end')
            .attr('class', 'legend-item')
            .style('stroke', (tooltipValue: TooltipValue) =>
                tooltipValue.color
            )
            .text((tooltipValue: TooltipValue) =>
                tooltipValue.value !== this.valueToEliminate
                    ? `${this.yFormatTick(tooltipValue.value)} ${this.tooltipData.unit}`
                    : ''
            );
    }

    private chartMousemove(event: MouseEvent): void {
        const { x, y } = this.container.getBoundingClientRect();
        const hoveredX: any = this.xScale.invert(event.clientX - x - this.dimensions.marginLeft);
        const hoveredY: any = this.yScale.invert(event.clientY - y - this.dimensions.marginTop);
        const getDistanceFromHoveredX = (d: any) => {
            return Math.abs(this.visibleSeries[0].xAccessor(d) - hoveredX);
        };
        const closestXIndex = d3.leastIndex(this.visibleSeries[0].dataPoints, (a, b) => (
            getDistanceFromHoveredX(a) - getDistanceFromHoveredX(b)
        ));
        const closestDataPoint = this.visibleSeries[0].dataPoints[closestXIndex];
        const closestXValue = this.visibleSeries[0].xAccessor(closestDataPoint);

        const closestYValues: number[] = [];
        let closestYIndex: number = -1;
        let minimum: number = Infinity;
        this.visibleSeries
            .forEach((serie: Serie, idx: number) => {
                const cdp = serie.dataPoints[closestXIndex];
                const dist = Math.abs(serie.yAccessor(cdp) - hoveredY);
                if (dist < minimum) {
                    minimum = dist;
                    closestYIndex = idx;
                }
                closestYValues.push(serie.yAccessor(cdp));
            });

        const closestYValue = closestYValues[closestYIndex];
        const tooltipPosX = this.xScale(closestXValue);
        const tooltipPosY = this.yScale(closestYValue);
        this.tooltipData = new TooltipData(this.xFormatTick(closestXValue));
        this.tooltipData.unit = this.series[0].unit;
        this.visibleSeries
            .forEach((serie, idx) => {
                if (closestYValues[idx] !== this.valueToEliminate) {
                    this.tooltipData.values.push(new TooltipValue(serie.label, serie.config.color, closestYValues[idx]));
                }
                else {
                    this.tooltipData.values.push(new TooltipValue(this.labelForEliminatedValue, serie.config.color, this.valueToEliminate));
                }
            });
        this.tooltipHeight = (1 + this.tooltipData.values.length) * this.tooltipLabelHeight + this.tooltipTitleHeight;
        this.tooltipMaxHeight = 2 * ((1 + this.series.length) * this.tooltipLabelHeight + this.tooltipTitleHeight);

        gsap.timeline()
            .to('#guide-hor', { duration: TimelineRawComponent.duration, attr: { y1: tooltipPosY, x2: tooltipPosX, y2: tooltipPosY }, ease: 'none' }, 0)
            .to('#guide-ver', { duration: TimelineRawComponent.duration, attr: { x1: tooltipPosX, x2: tooltipPosX }, ease: 'none' }, 0)
            .to('#tooltip-box', { duration: TimelineRawComponent.duration, css: { x: tooltipPosX, y: tooltipPosY }, ease: 'none' }, 0);

        this.orientation = new OutsideBoundary().bestPosition(
            this.dimensions.boundedWidth,
            this.dimensions.boundedHeight,
            tooltipPosX,
            tooltipPosY,
            this.tooltipWidth,
            this.tooltipHeight,
            this.tooltipArrowSize
        );

        switch (this.orientation) {
            case LegendTooltipPosEnum.TOP:
                this.tooltipSVGX = -1 * this.tooltipWidth / 2;
                this.tooltipSVGY = -1 * this.tooltipArrowSize - this.tooltipHeight;

                break;
            case LegendTooltipPosEnum.RIGHT:
                this.tooltipSVGX = this.tooltipArrowSize;
                this.tooltipSVGY = -1 * this.tooltipHeight / 2;

                break;

            case LegendTooltipPosEnum.BOTTOM:
                this.tooltipSVGX = -1 * this.tooltipWidth / 2;
                this.tooltipSVGY = this.tooltipArrowSize;

                break;

            case LegendTooltipPosEnum.LEFT:
                this.tooltipSVGX = -1 * this.tooltipWidth;
                this.tooltipSVGY = -1 * this.tooltipHeight / 2;

                break;
        }

        this.pathTooltip
            .attr('d', this.tooltipPath());
        this.svgTooltip
            .attr('x', this.tooltipSVGX)
            .attr('y', this.tooltipSVGY);
        this.titleTooltip
            .text(this.xFormatTick(closestXValue));
        this.drawTooltipValues();
    }

    private lineGenerator(serie: Serie, withElimination: boolean = false): string {
        const lineGenerator = d3
            .line()
            .defined((d: any) =>
                withElimination ? serie.yAccessor(d) !== this.valueToEliminate : true
            )
            .x(serie.xAccessorScaled)
            .y(serie.yAccessorScaled)
            .curve(this.interpolation);

        return lineGenerator(serie.dataPoints);
    }

    private toggleSerie(serie: Serie): void {
        if (this.visibleSeries.length === 1 && serie.isVisible) {
            return;
        }

        serie.isVisible = !serie.isVisible;

        this.adjustScales();
        this.drawYAxis();
        this.drawSeries();
        this.gpeLegendTextItems
            .style('text-decoration', (serie: Serie) =>
                serie.isVisible ? '' : 'line-through'
            );
        this.drawTooltip();
    }

    private tooltipPath(): string {
        switch (this.orientation) {
            case LegendTooltipPosEnum.TOP:
                return this.topTooltipPath(this.tooltipWidth, this.tooltipHeight, this.tooltipArrowSize, this.tooltipRadius);

            case LegendTooltipPosEnum.LEFT:
                return this.leftTooltipPath(this.tooltipWidth, this.tooltipHeight, this.tooltipArrowSize, this.tooltipRadius);

            case LegendTooltipPosEnum.RIGHT:
                return this.rightTooltipPath(this.tooltipWidth, this.tooltipHeight, this.tooltipArrowSize, this.tooltipRadius);

            case LegendTooltipPosEnum.BOTTOM:
                return this.bottomTooltipPath(this.tooltipWidth, this.tooltipHeight, this.tooltipArrowSize, this.tooltipRadius);
        }
    }

    private topTooltipPath(width: number, height: number, offset: number, radius: number): string {
        const left = -width / 2;
        const right = width / 2;
        const top = -offset - height;
        const bottom = -offset;

        return `
            M 0,0
            L ${-offset},${bottom}
            H ${left + radius}
            Q ${left},${bottom} ${left},${bottom - radius}
            V ${top + radius}
            Q ${left},${top} ${left + radius},${top}
            H ${right - radius}
            Q ${right},${top} ${right},${top + radius}
            V ${bottom - radius}
            Q ${right},${bottom} ${right - radius},${bottom}
            H ${offset}
            L 0,0 z
        `;
    }

    private bottomTooltipPath(width: number, height: number, offset: number, radius: number): string {
        const left = -width / 2;
        const right = width / 2;
        const top = offset;
        const bottom = offset + height;

        return `
            M 0,0
            L ${-offset},${top}
            H ${left + radius}
            Q ${left},${top} ${left},${top + radius}
            V ${bottom - radius}
            Q ${left},${bottom} ${left + radius},${bottom}
            H ${right - radius}
            Q ${right},${bottom} ${right},${bottom - radius}
            V ${top + radius}
            Q ${right},${top} ${right - radius},${top}
            H ${offset}
            L 0,0 z
      `;
    }

    private leftTooltipPath(width: number, height: number, offset: number, radius: number): string {
        const left = -offset - width;
        const right = -offset;
        const top = -height / 2;
        const bottom = height / 2;

        return `
            M 0,0
            L ${right},${-offset}
            V ${top + radius}
            Q ${right},${top} ${right - radius},${top}
            H ${left + radius}
            Q ${left},${top} ${left},${top + radius}
            V ${bottom - radius}
            Q ${left},${bottom} ${left + radius},${bottom}
            H ${right - radius}
            Q ${right},${bottom} ${right},${bottom - radius}
            V ${offset}
            L 0,0 z
      `;
    }

    private rightTooltipPath(width: number, height: number, offset: number, radius: number): string {
        const left = offset;
        const right = offset + width;
        const top = -height / 2;
        const bottom = height / 2;

        return `
            M 0,0
            L ${left},${-offset}
            V ${top + radius}
            Q ${left},${top} ${left + radius},${top}
            H ${right - radius}
            Q ${right},${top} ${right},${top + radius}
            V ${bottom - radius}
            Q ${right},${bottom} ${right - radius},${bottom}
            H ${left + radius}
            Q ${left},${bottom} ${left},${bottom - radius}
            V ${offset}
            L 0,0 z
      `;
    }
}
